import { WithDisposable } from '@blocksuite/affine/global/lit';
import { ShadowlessElement } from '@blocksuite/affine/std';
import { WebIcon } from '@blocksuite/icons/lit';
import type { Signal } from '@preact/signals-core';
import { html, nothing } from 'lit';
import { property } from 'lit/decorators.js';

import {
  localizedToolResultName,
  localizedToolText,
} from '../../utils/stream-objects';
import type { ToolError } from './type';

interface WebSearchToolCall {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  args: { query: string };
}

interface WebSearchResultItem {
  title: string;
  url: string;
  content?: string;
  snippet?: string;
  favicon?: string;
  publishedDate?: string;
  author?: string;
}

interface WebSearchToolResult {
  type: 'tool-result';
  toolCallId: string;
  toolName: string;
  args: { query: string };
  result:
    | { success: boolean; data?: WebSearchResultItem[] }
    | WebSearchResultItem[]
    | ToolError
    | null;
}

export class WebSearchTool extends WithDisposable(ShadowlessElement) {
  @property({ attribute: false })
  accessor data!: WebSearchToolCall | WebSearchToolResult;

  @property({ attribute: false })
  accessor width: Signal<number | undefined> | undefined;

  renderToolCall() {
    return html`
      <tool-call-card
        .name=${localizedToolText(
          'com.streamlain.ai.tool.web_search.running-query',
          `Searching the web for "${this.data.args.query}"`,
          { query: this.data.args.query }
        )}
        .icon=${WebIcon()}
      ></tool-call-card>
    `;
  }

  renderToolResult() {
    if (this.data.type !== 'tool-result') {
      return nothing;
    }

    // sl-jkhk: Unwrap { success, data } envelope from backend ToolResult
    const raw = this.data.result;
    const items: WebSearchResultItem[] | null = Array.isArray(raw)
      ? raw
      : raw &&
          typeof raw === 'object' &&
          'data' in raw &&
          Array.isArray(raw.data)
        ? raw.data
        : null;

    if (items && items.length > 0) {
      const results = items.map(item => ({
        title: item.title,
        icon: item.favicon || WebIcon(),
        content: item.content || item.snippet || '',
        href: item.url,
      }));
      const footerIcons = items
        .map(item => item.favicon)
        .filter(Boolean) as string[];

      return html`
        <tool-result-card
          .name=${localizedToolResultName('web_search', 'completed')}
          .icon=${WebIcon()}
          .footerIcons=${footerIcons}
          .results=${results}
          .width=${this.width}
        ></tool-result-card>
      `;
    }

    return html`
      <tool-call-failed
        .name=${localizedToolResultName('web_search', 'failed')}
        .icon=${WebIcon()}
      ></tool-call-failed>
    `;
  }

  protected override render() {
    const { data } = this;

    if (data.type === 'tool-call') {
      return this.renderToolCall();
    }
    if (data.type === 'tool-result') {
      return this.renderToolResult();
    }
    return nothing;
  }
}
